import React, { useCallback, useEffect, useState } from "react";
import { HardDrive, Link2, RefreshCw, Unlink, Send } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const GDRIVE = `${process.env.REACT_APP_BACKEND_URL}/api/oauth/drive`;

export function GoogleDriveCard({ onChange }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`${GDRIVE}/status`);
      setStatus(data);
      onChange?.(data);
    } catch { setStatus({ configured: true, connected: false }); }
  }, [onChange]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const g = params.get("gdrive");
    if (g === "connected") toast.success("Google Drive connected — your RallyPointGo folder is ready.");
    else if (g === "error") toast.error("Couldn't connect Google Drive. Please try again.");
    if (g) {
      params.delete("gdrive");
      const qs = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
    }
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`${GDRIVE}/connect`);
      window.location.href = data.authorization_url;
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setBusy(false); }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await api.post(`${GDRIVE}/disconnect`);
      toast.success("Google Drive disconnected.");
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const testConnection = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`${GDRIVE}/test`);
      if (data.ok) {
        toast.success(`Drive is working — test file added to "${data.folder}".`, { duration: 8000 });
        if (data.file_link) window.open(data.file_link, "_blank", "noopener");
      } else {
        toast.error(`Drive test failed: ${data.error}`, { duration: 14000 });
      }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!status) return null;
  if (status.configured === false) return null;

  return (
    <Card className="mb-6 border-slate-200 p-5 shadow-sm" data-testid="gdrive-card">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${status.connected ? "bg-emerald-100 text-emerald-600" : "bg-orange-100 text-orange-600"}`}>
            <HardDrive className="h-5 w-5" />
          </div>
          <div>
            <p className="font-heading font-semibold text-slate-800">Google Drive</p>
            {status.connected ? (
              <p className="text-sm text-slate-500" data-testid="gdrive-connected-email">
                Connected as <span className="font-medium text-slate-700">{status.email || "your Google account"}</span> · files upload straight to your Drive
              </p>
            ) : (
              <p className="text-sm text-slate-500">Connect once — RallyPointGo creates a folder in your Drive and every member upload lands there automatically.</p>
            )}
          </div>
        </div>

        {status.connected ? (
          <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
            <Button variant="outline" size="sm" onClick={testConnection} disabled={busy} data-testid="gdrive-test-btn" className="rounded-full">
              <Send className="mr-2 h-4 w-4" /> Test connection
            </Button>
            <Button variant="outline" size="sm" onClick={disconnect} disabled={busy} data-testid="gdrive-disconnect-btn" className="rounded-full text-red-600 hover:text-red-700">
              <Unlink className="mr-2 h-4 w-4" /> Disconnect
            </Button>
          </div>
        ) : (
          <Button onClick={connect} disabled={busy} data-testid="gdrive-connect-btn" variant="outline" className="rounded-full hover:bg-orange-500 hover:text-white">
            {busy ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <Link2 className="mr-2 h-4 w-4" />} Connect Google Drive
          </Button>
        )}
      </div>
    </Card>
  );
}

export default GoogleDriveCard;
